import { injectable } from 'inversify';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { SortingState } from "./SortingState";
import { SortingOptions } from "./SortingOptions";
import { SortingStateChangedRequestedEvent } from './events/SortingStateChangedRequestedEvent';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { EmbeddedStateChangedEvent } from '@/url/events/EmbeddedStateChangedEvent';
import { SortingStateCommittedEvent } from './events/SortingStateCommittedEvent';
import { SortingStateCommitIgnoredEvent } from './events/SortingStateCommitIgnoredEvent';
import { ISortingPayload } from './ISortingPayload';

@injectable()
export class SortingSelection implements ISubscriber<EmbeddedStateChangedEvent> {

	public readonly state: SortingState;

	public constructor(
		private readonly eventAggregationService: EventAggregationService
	) {
		this.state = new SortingState();

		this.eventAggregationService.subscribeTo(EmbeddedStateChangedEvent.EventType, this);
	}

	public notify(event: EmbeddedStateChangedEvent): void | Promise<void> {
		this.commit(event.state.sortingState, event.correlationId);
	}

	public processSelection(sortingPayload: ISortingPayload): void {

		if (!this.isValidOption(sortingPayload.value)) {
			return;
		}

		const newState = new SortingState(sortingPayload.value as SortingOptions);
		const stateChanged = !this.state.isEqual(newState);

		if (stateChanged) {
			this.publishChangeRequested(newState);
		}
	}

	public getSelected(): SortingOptions {
		return this.state.getSelected();
	}

	public isSelected(option: SortingOptions): boolean {
		return this.state.getSelected() === option;
	}

	private publishChangeRequested(state: SortingState, replaced = false): void {
		this.eventAggregationService.publish(new SortingStateChangedRequestedEvent(state, replaced));
	}

	private publishCommitted(state: SortingState, correlationId: number): void {
		this.eventAggregationService.publish(new SortingStateCommittedEvent(state, correlationId));
	}

	private publishCommitIgnored(correlationId: number): void {
		this.eventAggregationService.publish(new SortingStateCommitIgnoredEvent(correlationId));
	}

	private commit(other: SortingState, correlationId: number): void {
		if (this.state.isEqual(other)) {
			this.publishCommitIgnored(correlationId);
			return;
		}

		this.state.replaceWith(other);

		this.publishCommitted(this.state, correlationId);
	}

	private isValidOption(value: string): boolean {
		const validOptions = Object.values(SortingOptions).map(option => option.valueOf());

		return validOptions.includes(value);
	}
}
